/**
 * V2 source path: customer/midas/components/v2/ChatInputAreaV2.tsx
 * V1 source: shared/chatBot/components/ChatInputArea.tsx (capsule variant)
 * Strategy: logic-copied / JSX-rewritten-in-Tailwind
 * Verified no-MUI: yes
 * Phase: 1
 *
 * Copied from V1 verbatim (logic only):
 *   - `resizeImageForUpload` canvas resize helper (max 2048px, JPEG q=0.85)
 *   - `MAX_CHARS=5000`, `COUNTER_THRESHOLD=4500`, accepted MIME / extensions
 *   - `CAPSULE_MAX_HEIGHT=380` cap with internal scrollbar at cap
 *   - `autoResize` (set height to 0, read scrollHeight, clamp to min/max)
 *   - Enter sends, Shift+Enter newline keybinding handler
 *   - Image preview row + × remove + URL.revokeObjectURL cleanup
 *   - File input WITHOUT `capture` attribute (BUG-MIDAS-MOBILE-0414)
 *   - File-validation toast (unsupported format / too large)
 *   - rAF reset of textarea height after send (post-React state flush)
 *
 * Visual layer rewritten in Tailwind. The MUI capsule variant is the only
 * variant V2 ships — the compact widget variant lives in V1 ChatInputArea
 * and is consumed by `ChatWidget` / `ChatPanel` (overlay) which is untouched.
 *
 * Round 6 spec — send button is warm-graphite #2C2620 solid with bone
 * paper-plane (NOT amber gradient). Amber is reserved for content accents.
 *
 * Touch UX:
 *   - Phone: composer text 16px (no iOS auto-zoom on focus)
 *   - Phone: send/attach buttons 44×44 (TOUCH §1)
 *   - Phone: padding-bottom respects iOS safe area (notch / home indicator)
 *   - `-webkit-tap-highlight-color: transparent` on buttons; explicit
 *     active:scale-[0.96] for press feedback
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Camera, Send, X } from "lucide-react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "./ui/tooltip";

// ═══════════════════════════════════════════════════════════════
//  Constants — copied verbatim from V1 ChatInputArea.tsx
// ═══════════════════════════════════════════════════════════════

const MAX_CHARS = 5000;
const COUNTER_THRESHOLD = 4500;
const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB
const MAX_IMAGE_DIMENSION = 2048;
const JPEG_QUALITY = 0.85;
const ACCEPTED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
];
const ACCEPTED_IMAGE_EXTENSIONS = ".jpeg,.jpg,.png,.webp,.heic";

const CAPSULE_MAX_HEIGHT = 380; // ~19 lines before internal scroll
const CAPSULE_MIN_HEIGHT = 21; // 1 line (14px * 1.5 line-height) — auto-grows from 1 line
const CAPSULE_INITIAL_HEIGHT = 84; // 4 lines initial (Round 4 design target)

// ═══════════════════════════════════════════════════════════════
//  Image resize helper — copied verbatim from V1
// ═══════════════════════════════════════════════════════════════

const resizeImageForUpload = (file: File): Promise<File> => {
  return new Promise((resolve) => {
    const isPng = file.type === "image/png";
    const outputMime = isPng ? "image/png" : "image/jpeg";
    const outputExt = isPng ? ".png" : ".jpg";

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const { naturalWidth: w, naturalHeight: h } = img;

      if (w <= MAX_IMAGE_DIMENSION && h <= MAX_IMAGE_DIMENSION) {
        resolve(file);
        return;
      }

      const scale = Math.min(MAX_IMAGE_DIMENSION / w, MAX_IMAGE_DIMENSION / h);
      const newW = Math.round(w * scale);
      const newH = Math.round(h * scale);

      const canvas = document.createElement("canvas");
      canvas.width = newW;
      canvas.height = newH;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(file);
        return;
      }
      ctx.drawImage(img, 0, 0, newW, newH);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(file);
            return;
          }
          const stem = file.name.replace(/\.[^.]+$/, "");
          const resizedFile = new File([blob], `${stem}${outputExt}`, {
            type: outputMime,
            lastModified: Date.now(),
          });
          resolve(resizedFile);
        },
        outputMime,
        isPng ? undefined : JPEG_QUALITY
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(file);
    };

    img.src = objectUrl;
  });
};

// ═══════════════════════════════════════════════════════════════
//  Component
// ═══════════════════════════════════════════════════════════════

interface ChatInputAreaV2Props {
  onSend: (message: string, image?: File) => void;
  disabled?: boolean;
  /** When true, image upload is disabled (no machine context). */
  imageUploadDisabled?: boolean;
  /** Active machine name to render in the scope pill (optional). */
  scopeLabel?: string;
  /** Whether the scope pill should show the green "live" dot. */
  scopeLive?: boolean;
  isPhone?: boolean;
}

const ChatInputAreaV2: React.FC<ChatInputAreaV2Props> = ({
  onSend,
  disabled = false,
  imageUploadDisabled = false,
  scopeLabel,
  scopeLive = false,
  isPhone = false,
}) => {
  const { translate } = useTranslation("chatBot");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState("");
  const [attachedImage, setAttachedImage] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [snackError, setSnackError] = useState<string | null>(null);

  const charCount = value.length;
  const showCounter = charCount >= COUNTER_THRESHOLD;
  const hasText = value.trim().length > 0;
  const canSend = (hasText || attachedImage !== null) && !disabled;

  // Auto-resize textarea — verbatim from V1 capsule variant
  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "0px";
    const contentHeight = el.scrollHeight;
    const newHeight = Math.max(
      CAPSULE_MIN_HEIGHT,
      Math.min(contentHeight, CAPSULE_MAX_HEIGHT)
    );
    el.style.height = `${newHeight}px`;
    el.style.overflowY = contentHeight > CAPSULE_MAX_HEIGHT ? "auto" : "hidden";
  }, []);

  useEffect(() => {
    autoResize();
  }, [value, autoResize]);

  // Generate preview URL for attached image
  useEffect(() => {
    if (!attachedImage) {
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
      setImagePreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(attachedImage);
    setImagePreviewUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachedImage]);

  const handleSend = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed && !attachedImage) return;
    if (disabled) return;
    onSend(trimmed, attachedImage || undefined);
    setValue("");
    setAttachedImage(null);
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.style.height = `${CAPSULE_MIN_HEIGHT}px`;
      el.style.overflowY = "hidden";
    });
  }, [value, disabled, onSend, attachedImage]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newVal = e.target.value;
    if (newVal.length <= MAX_CHARS) {
      setValue(newVal);
    }
  };

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        setSnackError(translate("midas.imageUpload.unsupportedFormat"));
        e.target.value = "";
        return;
      }

      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        setSnackError(translate("midas.imageUpload.imageTooLarge"));
        e.target.value = "";
        return;
      }

      const resized = await resizeImageForUpload(file);
      setAttachedImage(resized);
      e.target.value = "";
    },
    [translate]
  );

  const handleRemoveImage = useCallback(() => {
    setAttachedImage(null);
  }, []);

  const handleAttachClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Counter color
  const counterColorClass =
    charCount >= MAX_CHARS
      ? "text-blue-500"
      : charCount >= COUNTER_THRESHOLD
        ? "text-amber-500"
        : "text-midas-muted-stone";

  // Touch sizing — phone uses 44×44 hit (TOUCH §1)
  const sendBtnSize = isPhone ? "h-11 w-11" : "h-9 w-9";
  const attachBtnSize = isPhone ? "h-11 w-11" : "h-8 w-8";

  return (
    <div
      className="bg-transparent"
      // iOS safe-area inset for the home indicator
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      {/* Hidden file input — no `capture` attribute (BUG-MIDAS-MOBILE-0414) */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_IMAGE_EXTENSIONS}
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Capsule */}
      <div
        className={`mx-2 my-2 sm:mx-3 sm:my-3 lg:mx-4 lg:mb-4 flex flex-col gap-2 rounded-2xl border border-midas-stone bg-midas-bone-light px-4 py-3 transition-all focus-within:border-midas-terracotta focus-within:shadow-[0_0_0_3px_rgba(212,87,43,0.12)] ${
          disabled ? "opacity-50" : ""
        }`}
      >
        {/* Scope pill row (top) */}
        {scopeLabel && (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-2 rounded-full border border-midas-stone bg-midas-bone px-2.5 py-0.5 font-sora text-[11px] font-medium text-midas-graphite">
              <span
                className={`inline-block h-1.5 w-1.5 rounded-full ${
                  scopeLive ? "bg-emerald-500" : "bg-midas-muted-stone"
                }`}
                aria-hidden="true"
              />
              {scopeLabel}
            </span>
          </div>
        )}

        {/* Image preview row */}
        {attachedImage && imagePreviewUrl && (
          <div className="flex items-center gap-2">
            <div className="relative h-14 w-14 flex-shrink-0 overflow-hidden rounded-lg border border-midas-stone">
              <img
                src={imagePreviewUrl}
                alt={attachedImage.name}
                className="block h-full w-full object-cover"
              />
              <button
                type="button"
                onClick={handleRemoveImage}
                aria-label="Remove attached image"
                className="absolute -right-1 -top-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white transition-colors hover:bg-black/80"
              >
                <X className="h-3 w-3" strokeWidth={2.5} />
              </button>
            </div>
            <span className="max-w-[200px] truncate font-sora text-[12px] text-midas-muted-stone">
              {attachedImage.name}
            </span>
          </div>
        )}

        {/* Textarea */}
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={translate("placeholder")}
          rows={1}
          // 16px on phone to prevent iOS auto-zoom on focus (TOUCH §3).
          // Desktop & tablet use 14px to match the molded editorial scale.
          className={`w-full resize-none border-0 bg-transparent p-0 font-sora ${
            isPhone ? "text-[16px]" : "text-[14px]"
          } font-normal leading-[1.5] text-midas-graphite outline-none placeholder:text-midas-muted-stone disabled:opacity-60`}
          style={{
            minHeight: `${CAPSULE_MIN_HEIGHT}px`,
            // Initial 4-line target — overridden by autoResize after first edit
            height:
              value.length === 0 ? `${CAPSULE_INITIAL_HEIGHT}px` : undefined,
            maxHeight: `${CAPSULE_MAX_HEIGHT}px`,
            overflowY: "hidden",
          }}
        />

        {/* Char counter */}
        {showCounter && (
          <div
            className={`mr-1 mt-0.5 text-right font-mono text-[10px] font-medium ${counterColorClass}`}
          >
            {translate("charCount", { count: charCount, max: MAX_CHARS })}
          </div>
        )}

        {/* Bottom row — attach left, send right */}
        <div className="mt-1 flex items-center justify-between">
          {/* Attach */}
          <Tooltip delayDuration={imageUploadDisabled ? 200 : 99999}>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <button
                  type="button"
                  onClick={handleAttachClick}
                  disabled={disabled || imageUploadDisabled}
                  aria-label={
                    isPhone
                      ? translate("midas.imageUpload.takePhoto")
                      : translate("midas.imageUpload.attachImage")
                  }
                  className={`inline-flex ${attachBtnSize} items-center justify-center rounded-md text-midas-warm-graphite transition-all hover:bg-midas-terracotta-soft hover:text-midas-terracotta disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-midas-warm-graphite active:scale-[0.96]`}
                  style={{ WebkitTapHighlightColor: "transparent" }}
                >
                  {isPhone ? (
                    <Camera className="h-5 w-5" strokeWidth={1.75} />
                  ) : (
                    <Plus className="h-5 w-5" strokeWidth={1.75} />
                  )}
                </button>
              </span>
            </TooltipTrigger>
            {imageUploadDisabled && (
              <TooltipContent>
                <span className="font-mono text-[11px]">
                  {translate("midas.imageUpload.selectMachineHint")}
                </span>
              </TooltipContent>
            )}
          </Tooltip>

          {/* Send — Round 6: warm-graphite solid + bone paper-plane (NOT amber). */}
          <button
            type="button"
            onClick={canSend ? handleSend : undefined}
            disabled={!canSend}
            aria-label="Send message"
            className={`inline-flex ${sendBtnSize} items-center justify-center rounded-md bg-midas-graphite-deep text-midas-bone transition-all active:scale-[0.96] ${
              canSend
                ? "cursor-pointer hover:scale-[1.04] hover:bg-midas-graphite"
                : "cursor-default opacity-35"
            }`}
            style={{ WebkitTapHighlightColor: "transparent" }}
          >
            <Send
              className="h-4 w-4"
              strokeWidth={1.75}
              style={{ transform: "translate(-1px, 1px)" }}
            />
          </button>
        </div>
      </div>

      {/* Error toast — pure Tailwind (no MUI Snackbar) */}
      {snackError && (
        <div
          role="alert"
          className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-md border border-red-300 bg-red-50 px-4 py-2 font-sora text-[13px] text-red-700 shadow-lg animate-in fade-in slide-in-from-bottom-2"
        >
          <div className="flex items-center gap-3">
            <span>{snackError}</span>
            <button
              type="button"
              onClick={() => setSnackError(null)}
              aria-label="Dismiss"
              className="font-mono text-[11px] uppercase opacity-70 hover:opacity-100"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatInputAreaV2;
